import { useEffect, useRef, useSyncExternalStore } from "react";

/**
 * "הגדרות קשורות" — shared state between the top-bar button, the side panel and the screens.
 *   useScreenSettings(items)   a screen offers its own settings (dialogs inside the screen) to the panel
 *   recent / return            last opened settings, and the "חזרה ל…" pill after leaving a screen
 */
export type ScreenSettingItem = {
  key: string;
  title: string;
  description?: string;
  icon?: string;
  onClick: () => void;
};

let screenItems: ScreenSettingItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = () => screenItems;

/** Shown first in the panel ("בתוך המסך הזה") while the screen is mounted. onClick runs after the panel closes. */
export function useScreenSettings(items: Array<ScreenSettingItem | null | false>) {
  const ref = useRef(items);
  ref.current = items;
  const sig = items.map((i) => (i ? `${i.key}:${i.title}` : "")).join("|");
  useEffect(() => {
    const own = (ref.current.filter(Boolean) as ScreenSettingItem[]).map((i) => ({
      ...i,
      onClick: () => (ref.current.find((x) => x && x.key === i.key) as ScreenSettingItem | undefined)?.onClick(),
    }));
    screenItems = [...screenItems.filter((i) => !own.some((o) => o.key === i.key)), ...own];
    emit();
    return () => {
      screenItems = screenItems.filter((i) => !own.includes(i));
      emit();
    };
  }, [sig]);
}

export function useScreenSettingsList(): ScreenSettingItem[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

const RECENT_KEY = "tb-related-recent";

export function readRecent(): string[] {
  try {
    return (JSON.parse(localStorage.getItem(RECENT_KEY) || "[]") as string[]) || [];
  } catch {
    return [];
  }
}

export function pushRecent(id: string) {
  try {
    const next = [id, ...readRecent().filter((x) => x !== id)].slice(0, 4);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export type RelatedReturn = { from: string; label: string; to: string; setting?: string };
const RETURN_KEY = "tb-related-return";

export function readReturn(): RelatedReturn | null {
  try {
    return (JSON.parse(sessionStorage.getItem(RETURN_KEY) || "null") as RelatedReturn | null) || null;
  } catch {
    return null;
  }
}

export function writeReturn(value: RelatedReturn | null) {
  try {
    if (value) sessionStorage.setItem(RETURN_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* ignore */
  }
}
